<template>
  <div class="v2-page">
    <div class="v2-manage-team">
      <div class="v2-manage-team__header">
        <h2 class="v2-heading-sm">
          Manage Team Members
        </h2>
        <v2-button
          v-if="!isSalesRep"
          @click="onAddTeammate"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle
              cx="8.5"
              cy="7"
              r="4"
            />
            <line
              x1="20"
              y1="8"
              x2="20"
              y2="14"
            />
            <line
              x1="23"
              y1="11"
              x2="17"
              y2="11"
            />
          </svg>
          Add Teammate
        </v2-button>
      </div>

      <template v-if="isLoadingTeammates">
        <div class="v2-manage-team__loading">
          <v2-spinner
            size="lg"
            label="Loading team members..."
          />
        </div>
      </template>

      <template v-else-if="!teamMatesList.length">
        <div class="v2-manage-team__empty">
          <span class="v2-text-sm v2-text-muted">No team members found</span>
        </div>
      </template>

      <template v-else>
        <v2-card no-padding>
          <v2-table
            :columns="tableColumns"
            :items="paginatedTeammates"
          >
            <template #cell(firstName)="{ item }">
              <span class="v2-font-medium">{{ item.firstName }}</span>
            </template>
            <template #cell(lastName)="{ item }">
              <span class="v2-font-medium">{{ item.lastName }}</span>
            </template>
            <template #cell(email)="{ item }">
              {{ item.email }}
            </template>
            <template #cell(role)="{ item }">
              <span class="v2-manage-team__role">{{ USER_ROLES_LABEL[item.role] || item.role }}</span>
            </template>
            <template #cell(status)="{ item }">
              <v2-badge :variant="parseStatusVariant(item.status)">
                {{ item.status }}
              </v2-badge>
            </template>
            <template #cell(notification)="{ item }">
              <button
                v-if="isNotificationPreferencesEnabled"
                class="v2-manage-team__icon-btn"
                title="Edit Notification Preferences"
                @click="onOpenNotification(item)"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </button>
              <span v-else>-</span>
            </template>
            <template #cell(_id)="{ item }">
              <div class="v2-manage-team__actions">
                <button
                  class="v2-manage-team__icon-btn"
                  title="Edit"
                  @click="onEditTeammate(item)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
                <button
                  class="v2-manage-team__icon-btn v2-manage-team__icon-btn--destructive"
                  title="Delete"
                  @click="onDeleteTeammate(item)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            </template>
          </v2-table>
        </v2-card>

        <v2-pagination
          :current-page="currentPage"
          :total-items="totalAdditionalUsers"
          :per-page="perPage"
          @page-change="currentPage = $event"
        />
      </template>

      <!-- Invite/Edit Modal -->
      <v2-invite-teammate-modal
        :show="showInviteModal"
        :edit-data="editTeammateData"
        :roles="teamMatesRoles"
        :sales-managers="salesManagers"
        :is-entity-admin="isEntityAdmin"
        :is-saving="isSaving"
        @close="onCloseInviteModal"
        @invite="onInviteTeammate"
        @update="onUpdateTeammate"
      />

      <!-- Delete Modal -->
      <v2-delete-teammate-modal
        :show="showDeleteModal"
        :teammate="selectedAdditionalUserForDelete"
        @close="showDeleteModal = false"
        @confirm="onTeammateDeleted"
      />

      <!-- Notification Preferences Modal -->
      <v2-notification-preferences-modal
        :show="showNotificationModal"
        :is-loading="isLoadingNotification"
        :is-saving="isSavingNotification"
        :notification-preferences="notificationPreferences"
        :original-notification-preferences="originalNotificationPreferences"
        :selected-teammate="selectedTeammateForNotification"
        :notification-preference-options="NOTIFICATION_PREFERENCE_OPTIONS"
        :notification-preference-types="NOTIFICATION_PREFERENCE_TYPES"
        :get-notification-preference-label="getNotificationPreferenceLabel"
        :requires-confirmation="requiresConfirmation"
        @close="onCloseNotificationModal"
        @update-preference="onUpdatePreference"
        @save="onSaveNotificationPreferences"
      />
    </div>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { useManageTeam } from '@/composables/useManageTeam'
import V2Button from '@/components/v2/V2Button.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Table from '@/components/v2/V2Table.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Pagination from '@/components/v2/V2Pagination.vue'
import V2InviteTeammateModal from './V2InviteTeammateModal.vue'
import V2DeleteTeammateModal from './V2DeleteTeammateModal.vue'
import V2NotificationPreferencesModal from './V2NotificationPreferencesModal.vue'

export default {
  name: 'V2ManageTeam',
  components: {
    V2Button,
    V2Card,
    V2Table,
    V2Badge,
    V2Spinner,
    V2Pagination,
    V2InviteTeammateModal,
    V2DeleteTeammateModal,
    V2NotificationPreferencesModal,
  },
  setup() {
    const team = useManageTeam()
    team.loadAllScopes()

    const showInviteModal = ref(false)
    const showDeleteModal = ref(false)
    const showNotificationModal = ref(false)
    const editTeammateData = ref(null)

    return {
      ...team,
      showInviteModal,
      showDeleteModal,
      showNotificationModal,
      editTeammateData,
    }
  },
  methods: {
    onAddTeammate() {
      this.editTeammateData = null
      this.clearFormData()
      this.showInviteModal = true
    },
    onEditTeammate(item) {
      this.editTeammateData = { ...item }
      this.populateFormForEdit(item)
      this.showInviteModal = true
    },
    onCloseInviteModal() {
      this.showInviteModal = false
      this.editTeammateData = null
      this.clearFormData()
    },
    async onInviteTeammate(payload) {
      const success = await this.sendInvite(payload)
      if (success) this.onCloseInviteModal()
    },
    async onUpdateTeammate(payload) {
      const success = await this.updateUser(payload)
      if (success) this.onCloseInviteModal()
    },
    onDeleteTeammate(item) {
      this.selectedAdditionalUserForDelete = item
      this.showDeleteModal = true
    },
    async onTeammateDeleted() {
      const success = await this.deleteUser()
      if (success) this.showDeleteModal = false
    },
    onOpenNotification(item) {
      this.selectedTeammateForNotification = item
      this.showNotificationModal = true
      this.fetchNotificationPreferences(item._id)
    },
    onCloseNotificationModal() {
      this.showNotificationModal = false
      this.clearNotificationData()
    },
    onUpdatePreference({ key, value }) {
      this.notificationPreferences = {
        ...this.notificationPreferences,
        [key]: value,
      }
    },
    async onSaveNotificationPreferences() {
      const success = await this.saveNotificationPreferences()
      if (success) this.onCloseNotificationModal()
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-manage-team {
  max-width: 960px;
  margin: 0 auto;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--v2-space-5);
  }

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
  }

  &__role {
    text-transform: capitalize;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-muted);
    }

    &--destructive {
      color: var(--v2-destructive);

      &:hover {
        background: rgba(229, 72, 77, 0.1);
      }
    }
  }
}
</style>
